import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { alertDeepLink, ALERT_SEVERITY_META } from "@/lib/alerts";

/**
 * AlertsWidget — Fase 8.1
 * Compact 3-line summary on Dashboard: counts by severity + last 3 critical.
 * Hidden if user lacks alerts:view.
 */
export function AlertsWidget({ tenantParam = {} }) {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const canView = hasPermission("alerts:view");

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    let alive = true;
    api.get("/alerts/summary", { params: tenantParam })
      .then(({ data }) => { if (alive) setSummary(data); })
      .catch(() => { if (alive) setSummary(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantParam?.tenant_id]);

  if (!canView) return null;
  if (loading) {
    return (
      <Card className="border-border" data-testid="alerts-widget">
        <CardContent className="p-4 flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando alertas…
        </CardContent>
      </Card>
    );
  }
  if (!summary || summary.total === 0) {
    return (
      <Card className="border-border bg-emerald-50/40" data-testid="alerts-widget-empty">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-emerald-700" />
            <div>
              <div className="text-sm font-medium text-emerald-900">Sin alertas activas</div>
              <div className="text-xs text-emerald-800">Todas las operaciones están al día.</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/alertas")} data-testid="alerts-widget-view-all">
            Ver todas <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { by_severity, total, top_critical = [] } = summary;
  const critCount = by_severity?.critical || 0;
  const warnCount = by_severity?.warning || 0;
  const infoCount = by_severity?.info || 0;

  return (
    <Card className="border-border" data-testid="alerts-widget">
      <CardContent className="p-4 space-y-3">
        {/* Row 1: header + total */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`h-5 w-5 ${critCount ? "text-red-600" : "text-amber-600"}`} />
            <div className="font-medium text-sm">
              Alertas operativas <span className="text-muted-foreground">({total})</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/alertas")} data-testid="alerts-widget-view-all">
            Ver todas <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>

        {/* Row 2: counts by severity */}
        <div className="flex flex-wrap gap-2 text-xs">
          <button
            onClick={() => navigate("/alertas?severity=critical")}
            className="px-2 py-1 rounded-md bg-red-50 text-red-800 border border-red-200 hover:bg-red-100"
            data-testid="alerts-widget-critical"
          >
            <span className="mono font-semibold">{critCount}</span> críticas
          </button>
          <button
            onClick={() => navigate("/alertas?severity=warning")}
            className="px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100"
            data-testid="alerts-widget-warning"
          >
            <span className="mono font-semibold">{warnCount}</span> warnings
          </button>
          <button
            onClick={() => navigate("/alertas?severity=info")}
            className="px-2 py-1 rounded-md bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100"
            data-testid="alerts-widget-info"
          >
            <span className="mono font-semibold">{infoCount}</span> info
          </button>
        </div>

        {/* Row 3: top 3 critical */}
        {top_critical.length > 0 && (
          <div className="border-t border-border pt-2 space-y-1" data-testid="alerts-widget-top">
            {top_critical.slice(0, 3).map((a) => {
              const meta = ALERT_SEVERITY_META[a.severity] || {};
              return (
                <button
                  key={a.id}
                  onClick={() => navigate(alertDeepLink(a))}
                  className="w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-slate-50 flex items-start gap-2"
                  data-testid={`alerts-widget-critical-item-${a.id}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${meta.dotClass || "bg-red-500"}`} />
                  <span className="line-clamp-1">{a.title}</span>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
